import { createApi } from '@reduxjs/toolkit/query/react';
import {
  SparePart,
  CreateSparePartBody,
  UpdateSparePartBody,
  UpdateSparePartStockBody,
  GetSparePartsParams,
  GetSparePartsResponse,
  SparePartHistoryItem,
  GetSparePartHistoryParams,
  GetSparePartHistoryResponse,
  ScooterType,
  CreateScooterTypeBody,
  UpdateScooterTypeBody,
  GetScooterTypesResponse,
  SparePartType,
  CreateSparePartTypeBody,
  UpdateSparePartTypeBody,
  SparePartOrder,
  CreateSparePartOrderBody,
  UpdateSparePartOrderBody,
  GetSparePartOrdersParams,
  GetSparePartOrdersResponse
} from '../../resources/types/sparePartsTypes';
import baseQuery from './baseQuery';

export interface Warehouse {
  _id: string;
  name: string;
  city?: string;
  coords?: {
    lat: number;
    lon: number;
  };
}

export interface CreateWarehouseBody {
  name: string;
  coords?: {
    lat: number;
    lon: number;
  };
}

export const sparePartsApi = createApi({
  reducerPath: 'sparePartsApi',
  baseQuery,
  tagTypes: ['SparePart', 'ScooterType', 'SparePartHistory', 'Warehouse', 'SparePartType', 'SparePartOrder'],
  endpoints: builder => ({
    getSpareParts: builder.query<GetSparePartsResponse, GetSparePartsParams>({
      query: params => ({
        url: 'scooter/spare_parts',
        params
      }),
      providesTags: ['SparePart']
    }),
    createSparePart: builder.mutation<SparePart, CreateSparePartBody>({
      query: body => ({
        url: 'scooter/spare_parts',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: SparePart }) {
        return response.data;
      },
      invalidatesTags: ['SparePart']
    }),
    updateSparePart: builder.mutation<SparePart, { id: string; body: UpdateSparePartBody }>({
      query: ({ id, body }) => ({
        url: `scooter/spare_parts/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: SparePart }) {
        return response.data;
      },
      invalidatesTags: ['SparePart']
    }),
    deleteSparePart: builder.mutation<void, string>({
      query: id => ({
        url: `scooter/spare_parts/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SparePart']
    }),
    updateSparePartStock: builder.mutation<SparePart, { sparePartId: string; body: UpdateSparePartStockBody }>({
      query: ({ sparePartId, body }) => ({
        url: `scooter/spare_parts/stock/${sparePartId}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: SparePart }) {
        return response.data;
      },
      invalidatesTags: ['SparePart', 'SparePartHistory']
    }),
    getSparePartHistory: builder.query<GetSparePartHistoryResponse, GetSparePartHistoryParams>({
      query: params => ({
        url: 'scooter/spare_parts/history',
        params
      }),
      providesTags: ['SparePartHistory']
    }),
    getScooterTypes: builder.query<ScooterType[], void>({
      query: () => 'scooter/scooter_types',
      transformResponse(response: GetScooterTypesResponse) {
        return response.data;
      },
      providesTags: ['ScooterType']
    }),
    createScooterType: builder.mutation<ScooterType, CreateScooterTypeBody>({
      query: body => ({
        url: 'scooter/scooter_types',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: ScooterType }) {
        return response.data;
      },
      invalidatesTags: ['ScooterType']
    }),
    updateScooterType: builder.mutation<ScooterType, { id: string; body: UpdateScooterTypeBody }>({
      query: ({ id, body }) => ({
        url: `scooter/scooter_types/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: ScooterType }) {
        return response.data;
      },
      invalidatesTags: ['ScooterType']
    }),
    deleteScooterType: builder.mutation<void, string>({
      query: id => ({
        url: `scooter/scooter_types/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['ScooterType']
    }),
    getWarehouses: builder.query<Warehouse[], void>({
      query: () => 'warehouse',
      transformResponse(response: { data: Warehouse[] }) {
        return response.data;
      },
      providesTags: ['Warehouse']
    }),
    createWarehouse: builder.mutation<Warehouse, CreateWarehouseBody>({
      query: body => ({
        url: 'warehouse',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: Warehouse }) {
        return response.data;
      },
      invalidatesTags: ['Warehouse']
    }),
    updateWarehouse: builder.mutation<Warehouse, { id: string; body: CreateWarehouseBody }>({
      query: ({ id, body }) => ({
        url: `warehouse/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: Warehouse }) {
        return response.data;
      },
      invalidatesTags: ['Warehouse']
    }),
    deleteWarehouse: builder.mutation<void, string>({
      query: id => ({
        url: `warehouse/${id}`,
        method: 'POST'
      }),
      invalidatesTags: ['Warehouse']
    }),
    getSparePartTypes: builder.query<SparePartType[], void>({
      query: () => 'scooter/spare_part_types',
      transformResponse(response: { data: SparePartType[] }) {
        return response.data;
      },
      providesTags: ['SparePartType']
    }),
    createSparePartType: builder.mutation<SparePartType, CreateSparePartTypeBody>({
      query: body => ({
        url: 'scooter/spare_part_types',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: SparePartType }) {
        return response.data;
      },
      invalidatesTags: ['SparePartType']
    }),
    updateSparePartType: builder.mutation<SparePartType, { id: string; body: UpdateSparePartTypeBody }>({
      query: ({ id, body }) => ({
        url: `scooter/spare_part_types/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: SparePartType }) {
        return response.data;
      },
      invalidatesTags: ['SparePartType']
    }),
    deleteSparePartType: builder.mutation<void, string>({
      query: id => ({
        url: `scooter/spare_part_types/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SparePartType']
    }),
    getSparePartOrders: builder.query<GetSparePartOrdersResponse, GetSparePartOrdersParams>({
      query: params => ({
        url: 'scooter/spare_part_orders',
        params
      }),
      providesTags: ['SparePartOrder']
    }),
    createSparePartOrder: builder.mutation<SparePartOrder, CreateSparePartOrderBody>({
      query: body => ({
        url: 'scooter/spare_part_orders',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: SparePartOrder }) {
        return response.data;
      },
      invalidatesTags: ['SparePartOrder', 'SparePart']
    }),
    updateSparePartOrder: builder.mutation<SparePartOrder, { id: string; body: UpdateSparePartOrderBody }>({
      query: ({ id, body }) => ({
        url: `scooter/spare_part_orders/${id}`,
        method: 'PUT',
        body
      }),
      transformResponse(response: { data: SparePartOrder }) {
        return response.data;
      },
      invalidatesTags: ['SparePartOrder', 'SparePart']
    })
  })
});

export const {
  useGetSparePartsQuery,
  useLazyGetSparePartsQuery,
  useCreateSparePartMutation,
  useUpdateSparePartMutation,
  useDeleteSparePartMutation,
  useUpdateSparePartStockMutation,
  useGetSparePartHistoryQuery,
  useLazyGetSparePartHistoryQuery,
  useGetScooterTypesQuery,
  useCreateScooterTypeMutation,
  useUpdateScooterTypeMutation,
  useDeleteScooterTypeMutation,
  useGetWarehousesQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  useGetSparePartTypesQuery,
  useCreateSparePartTypeMutation,
  useUpdateSparePartTypeMutation,
  useDeleteSparePartTypeMutation,
  useGetSparePartOrdersQuery,
  useLazyGetSparePartOrdersQuery,
  useCreateSparePartOrderMutation,
  useUpdateSparePartOrderMutation
} = sparePartsApi;
